"use client";

import { Box, Flex, Grid, Icon, Text } from "@chakra-ui/react";
import { useTranslations, useLocale } from "next-intl";
import { NextLink } from "@/_components/ui/NextLink";
import {
  TbShieldCheck,
  TbFileDescription,
  TbCreditCardRefund,
} from "react-icons/tb";
import { IoChevronForward } from "react-icons/io5";
import { HELP_CENTER_IDS } from "../_constants/help-center.ids";

export function PolicySection() {
  const t = useTranslations("landing.help_center.policy_section");
  const tPolicies = useTranslations("landing.policies");
  const locale = useLocale();

  const policies = [
    {
      icon: TbShieldCheck,
      title: tPolicies("privacy.title"),
      description: t("privacy_desc"),
      href: `/${locale}/help-center/privacy-policy`,
      color: "blue",
    },
    {
      icon: TbFileDescription,
      title: tPolicies("terms.title"),
      description: t("terms_desc"),
      href: `/${locale}/help-center/terms-of-service`,
      color: "purple",
    },
    {
      icon: TbCreditCardRefund,
      title: tPolicies("refund.title"),
      description: t("refund_desc"),
      href: `/${locale}/help-center/refund-policy`,
      color: "green",
    },
  ];

  return (
    <Box id={HELP_CENTER_IDS.policy.section}>
      <Flex id={HELP_CENTER_IDS.policy.labelRow} align="center" gap={2} mb={3}>
        <Box
          id={HELP_CENTER_IDS.policy.labelBar}
          w="20px"
          h="0.5"
          bg="primary.600"
          borderRadius="1px"
        />
        <Text
          id={HELP_CENTER_IDS.policy.label}
          fontSize="11px"
          fontWeight="bold"
          letterSpacing="0.12em"
          textTransform="uppercase"
          color="primary.600"
        >
          {t("label")}
        </Text>
      </Flex>

      <Text
        id={HELP_CENTER_IDS.policy.title}
        fontFamily="heading"
        fontSize={{ base: "xl", md: "2xl" }}
        fontWeight="bold"
        color="neutral.900"
        letterSpacing="-0.5px"
        mb={3}
      >
        {t("title")}
      </Text>

      <Text
        id={HELP_CENTER_IDS.policy.description}
        fontSize="15px"
        color="neutral.500"
        lineHeight={1.7}
        mb={8}
      >
        {t("description")}
      </Text>

      <Grid
        id={HELP_CENTER_IDS.policy.grid}
        templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
        gap={5}
      >
        {policies.map((policy, index) => (
          <NextLink
            key={policy.href}
            id={HELP_CENTER_IDS.policy.link(index + 1)}
            href={policy.href}
            _hover={{ textDecoration: "none" }}
          >
            <Box
              id={HELP_CENTER_IDS.policy.card(index + 1)}
              bg="white"
              border="1px solid"
              borderColor="neutral.100"
              borderRadius="2xl"
              p={{ base: 5, md: 7 }}
              h="full"
              w="full"
              transition="all 0.25s ease"
              cursor="pointer"
              shadow="0 1px 3px rgba(0,0,0,0.04)"
              _hover={{
                borderColor: "primary.200",
                shadow: "0 8px 32px rgba(0,0,0,0.08)",
                transform: "translateY(-4px)",
              }}
            >
              <Flex
                id={HELP_CENTER_IDS.policy.cardIcon(index + 1)}
                align="center"
                justify="center"
                w={12}
                h={12}
                borderRadius="xl"
                bg="primary.50"
                mb={5}
              >
                <Icon as={policy.icon} boxSize={6} color="primary.500" />
              </Flex>

              <Text
                id={HELP_CENTER_IDS.policy.cardTitle(index + 1)}
                fontFamily="heading"
                fontSize="lg"
                fontWeight="600"
                color="neutral.900"
                mb={2}
                letterSpacing="-0.3px"
              >
                {policy.title}
              </Text>

              <Text
                id={HELP_CENTER_IDS.policy.cardDescription(index + 1)}
                fontSize="13px"
                color="neutral.500"
                lineHeight={1.6}
                mb={4}
              >
                {policy.description}
              </Text>

              <Flex
                id={HELP_CENTER_IDS.policy.cardReadmore(index + 1)}
                align="center"
                gap={1}
                color="primary.500"
              >
                <Text
                  id={HELP_CENTER_IDS.policy.cardReadmoreText(index + 1)}
                  fontSize="13px"
                  fontWeight="500"
                >
                  Read more
                </Text>
                <Icon as={IoChevronForward} boxSize={3.5} />
              </Flex>
            </Box>
          </NextLink>
        ))}
      </Grid>
    </Box>
  );
}
